import React, { useState, useRef, useEffect, Fragment } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { XMarkIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import { Button } from "components/ui";
import CustomCreatableDropdown from "custom-components/shared/CustomCreatableDropdown";
import { toast } from "sonner";
import { getUserIdFromToken } from "utils/tokenUtils";
import { updateLeadQuotationStatus } from "services/quotationService";

const QuotationStatusChangeModal = ({
  isOpen,
  onClose,
  mode,
  quotation,
  onStatusChanged,
  workStatusOptions = [],
  costStatusOptions = [],
  addingWorkStatus = false,
  addingCostStatus = false,
  handleCreateWorkStatus,
  handleEditWorkStatus,
  handleDeleteWorkStatus,
  handleCreateCostStatus,
  handleEditCostStatus,
  handleDeleteCostStatus,
  isStatusOptionActionable,
}) => {
  const [statusId, setStatusId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const saveRef = useRef(null);

  useEffect(() => {
    if (isOpen && quotation) {
      if (mode === "status") {
        setStatusId(quotation.WorkStatusId ? String(quotation.WorkStatusId) : "");
      } else {
        setStatusId(quotation.CostStatusId ? String(quotation.CostStatusId) : "");
      }
    }
  }, [isOpen, quotation, mode]);

  const handleSubmit = async () => {
    if (!statusId) {
      toast.info(`Please select a ${mode === "status" ? "status" : "cost status"}`);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        id: quotation.Id,
        workStatusId: mode === "status" ? parseInt(statusId, 10) : null,
        costStatusId: mode === "costStatus" ? parseInt(statusId, 10) : null,
        userId: getUserIdFromToken(),
      };

      const result = await updateLeadQuotationStatus(payload);
      if (result.success) {
        toast.success(`${mode === "status" ? "Work status" : "Cost status"} updated successfully`);
        if (onStatusChanged) {
          onStatusChanged();
        }
        onClose();
      } else {
        toast.error(result.error || "Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    } finally {
      setIsSubmitting(false);
    }
  };

  const title = mode === "status" ? "Change Work Status" : "Change Cost Status";
  const label = mode === "status" ? "Work Status *" : "Cost Status *";
  const selectPlaceholder = mode === "status" ? "Search or select Work Status..." : "Search or select Cost Status...";
  const menuHeader = mode === "status" ? "Select Work Status" : "Select Cost Status";
  const addNewText = mode === "status" ? "+ Add New Status" : "+ Add New Cost Status";

  const options = mode === "status" ? workStatusOptions : costStatusOptions;
  const selectedValue = options.find(opt => opt.value === statusId) || null;
  const isLoading = mode === "status" ? addingWorkStatus : addingCostStatus;
  const onCreateOption = mode === "status" ? handleCreateWorkStatus : handleCreateCostStatus;
  const onEditOption = mode === "status" ? handleEditWorkStatus : handleEditCostStatus;
  const onDeleteOption = mode === "status" ? handleDeleteWorkStatus : handleDeleteCostStatus;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[200]" onClose={onClose} initialFocus={saveRef}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="relative w-full max-w-md transform overflow-hidden rounded-[20px] bg-white shadow-2xl transition-all dark:bg-gray-900 flex flex-col outline-none focus:outline-none">
                <div className="flex items-center justify-between border-b border-gray-150 dark:border-gray-800 px-6 py-4 flex-shrink-0">
                  <DialogTitle
                    as="h3"
                    className="flex items-center gap-2 text-base font-bold text-gray-900 dark:text-white uppercase tracking-wider"
                  >
                    <CheckCircleIcon className="h-5 w-5 text-blue-600" />
                    {title}
                  </DialogTitle>
                  <Button
                    onClick={onClose}
                    variant="flat"
                    isIcon
                    className="w-8 h-8 rounded-full text-gray-400 hover:text-gray-650 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </Button>
                </div>

                <div className="flex-1 px-6 py-6 overflow-y-auto">
                  <p className="mb-4 text-sm text-gray-650 dark:text-gray-400 leading-relaxed">
                    Update the {mode === "status" ? "work status" : "cost status"} for this client note.
                  </p>
                  
                  <div className="space-y-1.5">
                    <label className="block text-[11px] font-semibold text-slate-500 dark:text-dark-300">
                      {label}
                    </label>
                    <CustomCreatableDropdown
                      options={options}
                      value={selectedValue}
                      onChange={(newValue) => setStatusId(newValue ? newValue.value : "")}
                      onCreateOption={async (name) => {
                        const res = await onCreateOption(name);
                        if (res && res.success && res.optionId) {
                          setStatusId(res.optionId.toString());
                        }
                      }}
                      onEditOption={onEditOption}
                      onDeleteOption={onDeleteOption}
                      isOptionActionable={isStatusOptionActionable}
                      placeholder={selectPlaceholder}
                      isLoading={isLoading}
                      menuHeader={menuHeader}
                      addNewText={addNewText}
                      bypassInternalConfirm={true}
                    />
                  </div>
                </div>
                
                <div className="bg-gray-50 dark:bg-gray-800 px-6 py-4 flex items-center justify-end gap-3 border-t border-gray-150 dark:border-gray-800 flex-shrink-0">
                  <Button
                    onClick={onClose}
                    variant="outlined"
                    color="neutral"
                    className="px-5 py-2 text-xs font-bold rounded-lg"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    color="primary"
                    ref={saveRef}
                    className="min-w-[100px] px-5 py-2 text-xs font-bold rounded-lg"
                    disabled={isSubmitting || !statusId}
                  >
                    {isSubmitting ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                        Saving...
                      </div>
                    ) : (
                      "Save"
                    )}
                  </Button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default QuotationStatusChangeModal;
